import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import HomeRoundedIcon         from '@mui/icons-material/HomeRounded'
import HomeWorkRoundedIcon     from '@mui/icons-material/HomeWorkRounded'
import ApartmentRoundedIcon    from '@mui/icons-material/ApartmentRounded'
import AccountTreeRoundedIcon  from '@mui/icons-material/AccountTreeRounded'
import VerifiedRoundedIcon     from '@mui/icons-material/VerifiedRounded'
import AssignmentRoundedIcon   from '@mui/icons-material/AssignmentRounded'

const ICON_BY_TYPE = {
  home:      HomeRoundedIcon,
  dashboard: HomeRoundedIcon,
  portfolio: AccountTreeRoundedIcon,
  estate:    ApartmentRoundedIcon,
  bp:        HomeWorkRoundedIcon,
  compliance:VerifiedRoundedIcon,
  programme: AssignmentRoundedIcon,
}

/**
 * Consistent breadcrumb used across the BP-detail / compliance / programme
 * flow. Each segment is `{ type, label, to? }` — `to` makes it a link,
 * absence keeps it as a non-clickable current page indicator.
 *
 * Props:
 *   items: [{ type, label, to? }, ...]
 *   compact?: boolean — denser styling when used inside a tile/card
 */
const Breadcrumb = ({ items = [], compact = false }) => {
  const navigate = useNavigate()
  if (!items.length) return null

  return (
    <Bar $compact={compact} aria-label='Breadcrumb'>
      {items.map((seg, i) => {
        const Icon = ICON_BY_TYPE[seg.type] || ICON_BY_TYPE.home
        const isLast = i === items.length - 1
        const clickable = !!seg.to && !isLast
        return (
          <React.Fragment key={`${seg.type}-${seg.label}-${i}`}>
            {i > 0 && <Sep><ChevronRightRoundedIcon style={{ fontSize: 16 }} /></Sep>}
            <Segment
              $current={isLast}
              $clickable={clickable}
              role={clickable ? 'link' : undefined}
              onClick={clickable ? () => navigate(seg.to) : undefined}
            >
              <Icon style={{ fontSize: compact ? 13 : 15 }} />
              <span>{seg.label}</span>
            </Segment>
          </React.Fragment>
        )
      })}
    </Bar>
  )
}

export default Breadcrumb

const Bar = styled.nav`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ $compact }) => $compact ? '2px' : '4px'};
  font-size: ${({ $compact }) => $compact ? '11.5px' : '12.5px'};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Sep = styled.span`
  display: inline-flex; align-items: center;
  color: #c5c5cd;
`

const Segment = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 8px;
  border-radius: 8px;
  font-weight: ${({ $current }) => $current ? 700 : 600};
  color: ${({ $current, $clickable, theme }) =>
    $current ? theme.colors.haiti
    : $clickable ? theme.colors.grey[800]
    : '#9aa0aa'};
  background: ${({ $current }) => $current ? 'rgba(12,18,41,0.04)' : 'transparent'};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 160ms ease, color 160ms ease;
  &:hover {
    background: ${({ $clickable }) => $clickable ? 'rgba(51,190,204,0.10)' : 'rgba(12,18,41,0.04)'};
    color: ${({ $clickable, theme }) => $clickable ? theme.colors.haiti : 'inherit'};
  }
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
